import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, FileText, PenTool, ClipboardCheck, AlertTriangle, 
  Lightbulb, Loader2, ArrowLeft, CheckCircle, XCircle
} from 'lucide-react';
import { client } from '../api/client';

interface StudyProgram {
  id: number;
  topicId: number;
  title: string;
  grade: number;
  classification: any;
  preview: any;
  workbook: any;
  test: any;
  mistakeBook: any[];
  difficulties: any[];
  generatedAt: string;
  updatedAt: string;
  version: number;
}

const TABS = [
  { id: 'classification', label: '分类整理', icon: BookOpen },
  { id: 'preview', label: '预习', icon: FileText },
  { id: 'workbook', label: '练习册', icon: PenTool },
  { id: 'test', label: '测试', icon: ClipboardCheck },
  { id: 'mistakeBook', label: '错题本', icon: AlertTriangle },
  { id: 'difficulties', label: '难点解答', icon: Lightbulb },
];

export function StudyProgramPage() {
  const { id } = useParams<{ id: string }>();
  const [program, setProgram] = useState<StudyProgram | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('classification');
  const [workbookSubTab, setWorkbookSubTab] = useState<'basic' | 'intermediate' | 'advanced'>('basic');
  const [showAnswer, setShowAnswer] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchStudyProgram();
  }, [id]);

  const fetchStudyProgram = async () => {
    try {
      setLoading(true);
      const res = await client.api.fetch(`/api/study-programs/${id}`);
      if (!res.ok) {
        throw new Error('Failed to fetch study program');
      }
      const data = await res.json();
      setProgram(data.data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const toggleAnswer = (key: string) => {
    setShowAnswer(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-600">加载学习程序中...</p>
        </div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-slate-600">{error || '未找到学习程序'}</p>
          <a href="/materials" className="mt-4 inline-block text-blue-500 hover:underline">
            返回资料列表
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <a href="/materials" className="inline-flex items-center text-slate-600 hover:text-blue-500 mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" />
            返回资料列表
          </a>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{program.title}</h1>
              <p className="text-slate-600 mt-1">Year {program.grade} · IB 学习程序 · 版本 {program.version}</p>
            </div>
            <div className="text-right text-sm text-slate-500">
              <p>生成时间: {new Date(program.generatedAt).toLocaleDateString()}</p>
              <p>更新时间: {new Date(program.updatedAt).toLocaleDateString()}</p>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
          <div className="border-b border-slate-200">
            <div className="flex overflow-x-auto">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-6">
            {/* Classification Tab */}
            <div style={{ display: activeTab === 'classification' ? 'block' : 'none' }}>
              <ClassificationSection data={program.classification} />
            </div>

            {/* Preview Tab */}
            <div style={{ display: activeTab === 'preview' ? 'block' : 'none' }}>
              <PreviewSection data={program.preview} />
            </div>

            {/* Workbook Tab */}
            <div style={{ display: activeTab === 'workbook' ? 'block' : 'none' }}>
              <WorkbookSection 
                data={program.workbook} 
                subTab={workbookSubTab}
                setSubTab={setWorkbookSubTab}
                showAnswer={showAnswer}
                toggleAnswer={toggleAnswer}
              />
            </div>

            {/* Test Tab */}
            <div style={{ display: activeTab === 'test' ? 'block' : 'none' }}>
              <TestSection data={program.test} showAnswer={showAnswer} toggleAnswer={toggleAnswer} />
            </div>

            {/* Mistake Book Tab */}
            <div style={{ display: activeTab === 'mistakeBook' ? 'block' : 'none' }}>
              <MistakeBookSection data={program.mistakeBook} showAnswer={showAnswer} toggleAnswer={toggleAnswer} />
            </div>

            {/* Difficulties Tab */}
            <div style={{ display: activeTab === 'difficulties' ? 'block' : 'none' }}>
              <DifficultiesSection data={program.difficulties} />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// Classification Section Component
function ClassificationSection({ data }: { data: any }) {
  if (!data) return <p className="text-slate-500">暂无数据</p>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-3">核心知识点</h3>
        <div className="grid gap-2">
          {data.coreKnowledge?.map((item: any, idx: number) => (
            <div key={idx} className={`p-3 rounded-lg ${
              item.level === 'basic' ? 'bg-green-50 border border-green-200' :
              item.level === 'core' ? 'bg-blue-50 border border-blue-200' :
              'bg-purple-50 border border-purple-200'
            }`}>
              <span className="text-xs font-medium text-slate-500 uppercase mr-2">
                {item.level === 'basic' ? '基础' : item.level === 'core' ? '核心' : '拓展'}
              </span>
              <span className="text-slate-700">{item.point}</span>
            </div>
          ))}
        </div>
      </div>

      {data.ibKeyConcepts && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-3">IB Key Concepts</h3>
          <div className="flex flex-wrap gap-2">
            {data.ibKeyConcepts.map((concept: string, idx: number) => (
              <span key={idx} className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
                {concept}
              </span>
            ))}
          </div>
        </div>
      )}

      {data.commonMistakes && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-3">常见错误</h3>
          <div className="space-y-2">
            {data.commonMistakes.map((item: any, idx: number) => (
              <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="font-medium text-red-800">{item.mistake}</p>
                <p className="text-sm text-red-600 mt-1">原因: {item.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Preview Section Component
function PreviewSection({ data }: { data: any }) {
  if (!data) return <p className="text-slate-500">暂无数据</p>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-3">预习目标</h3>
        <ul className="space-y-2">
          {data.objectives?.map((obj: string, idx: number) => (
            <li key={idx} className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-slate-700">{obj}</span>
            </li>
          ))}
        </ul>
      </div>

      {data.framework && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-3">知识框架</h3>
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <h4 className="font-medium text-slate-900 mb-3">{data.framework.main}</h4>
            <div className="grid md:grid-cols-2 gap-4">
              {data.framework.branches?.map((branch: any, idx: number) => (
                <div key={idx} className="bg-white p-3 rounded-lg border border-slate-200">
                  <h5 className="font-medium text-slate-800 mb-2">{branch.title}</h5>
                  <ul className="space-y-1">
                    {branch.points?.map((point: string, pIdx: number) => (
                      <li key={pIdx} className="text-sm text-slate-600">• {point}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {data.guidingQuestions && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-3">预习引导问题</h3>
          <div className="space-y-2">
            {data.guidingQuestions.map((q: string, idx: number) => (
              <div key={idx} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-blue-800">{q}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Workbook Section Component
function WorkbookSection({ 
  data, 
  subTab, 
  setSubTab, 
  showAnswer, 
  toggleAnswer 
}: { 
  data: any; 
  subTab: string;
  setSubTab: (tab: any) => void;
  showAnswer: Record<string, boolean>;
  toggleAnswer: (key: string) => void;
}) {
  if (!data) return <p className="text-slate-500">暂无数据</p>;

  const levels = [
    { id: 'basic', label: '基础题', color: 'green' },
    { id: 'intermediate', label: '应用题', color: 'blue' },
    { id: 'advanced', label: '分析题', color: 'purple' },
  ];

  const questions = data[subTab] || [];

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {levels.map((level) => (
          <button
            key={level.id}
            onClick={() => setSubTab(level.id as any)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              subTab === level.id
                ? `bg-${level.color}-100 text-${level.color}-800 border border-${level.color}-300`
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {level.label} ({data[level.id]?.length || 0})
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {questions.map((q: any, idx: number) => {
          const answerKey = `workbook-${subTab}-${idx}`;
          return (
            <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-start justify-between mb-2">
                <span className="text-xs font-medium px-2 py-1 bg-slate-200 rounded">
                  {q.ibLevel}
                </span>
                <span className="text-xs text-slate-500">{q.knowledgePoint}</span>
              </div>
              <p className="text-slate-800 font-medium mb-3">{idx + 1}. {q.question}</p>
              
              {q.options && (
                <div className="space-y-1 mb-3">
                  {q.options.map((opt: string, optIdx: number) => (
                    <div key={optIdx} className="text-sm text-slate-600 pl-4">{opt}</div>
                  ))}
                </div>
              )}

              <button
                onClick={() => toggleAnswer(answerKey)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {showAnswer[answerKey] ? '隐藏答案' : '显示答案'}
              </button>

              {showAnswer[answerKey] && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="font-medium text-green-800">答案: {q.answer}</p>
                  {q.explanation && (
                    <p className="text-sm text-green-700 mt-1">解析: {q.explanation}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Test Section Component
function TestSection({ data, showAnswer, toggleAnswer }: { data: any; showAnswer: Record<string, boolean>; toggleAnswer: (key: string) => void }) {
  if (!data) return <p className="text-slate-500">暂无数据</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 p-4 bg-slate-50 rounded-lg">
        <div>
          <h3 className="font-semibold text-slate-900">{data.title}</h3>
          <p className="text-sm text-slate-600">总分: {data.totalScore}分 · 建议用时: {data.timeMinutes}分钟</p>
        </div>
      </div>

      {data.multipleChoice && data.multipleChoice.length > 0 && (
        <div className="mb-6">
          <h4 className="font-medium text-slate-800 mb-3">选择题 ({data.multipleChoice.length}题)</h4>
          <div className="space-y-3">
            {data.multipleChoice.map((q: any, idx: number) => {
              const answerKey = `test-mc-${idx}`;
              return (
                <div key={idx} className="p-3 bg-white rounded-lg border border-slate-200">
                  <p className="text-slate-800 mb-2">{idx + 1}. {q.question} ({q.score}分)</p>
                  <div className="space-y-1 mb-2">
                    {q.options?.map((opt: string, optIdx: number) => (
                      <div key={optIdx} className="text-sm text-slate-600 pl-4">{opt}</div>
                    ))}
                  </div>
                  <button
                    onClick={() => toggleAnswer(answerKey)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {showAnswer[answerKey] ? '隐藏答案' : '显示答案'}
                  </button>
                  {showAnswer[answerKey] && (
                    <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                      <span className="font-medium text-green-800">答案: {q.answer}</span>
                      {q.explanation && <span className="text-green-700 ml-2">- {q.explanation}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.shortAnswer && data.shortAnswer.length > 0 && (
        <div>
          <h4 className="font-medium text-slate-800 mb-3">简答题 ({data.shortAnswer.length}题)</h4>
          <div className="space-y-3">
            {data.shortAnswer.map((q: any, idx: number) => {
              const answerKey = `test-sa-${idx}`;
              return (
                <div key={idx} className="p-3 bg-white rounded-lg border border-slate-200">
                  <p className="text-slate-800 mb-2">{idx + 1}. {q.question} ({q.score}分)</p>
                  <button
                    onClick={() => toggleAnswer(answerKey)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {showAnswer[answerKey] ? '隐藏答案' : '显示答案'}
                  </button>
                  {showAnswer[answerKey] && (
                    <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                      <p className="font-medium text-green-800">答案: {q.answer}</p>
                      {q.markingPoints && (
                        <div className="mt-1">
                          <span className="text-green-700">采分点: </span>
                          {q.markingPoints.join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Mistake Book Section Component
function MistakeBookSection({ data, showAnswer, toggleAnswer }: { data: any[]; showAnswer: Record<string, boolean>; toggleAnswer: (key: string) => void }) {
  if (!data || data.length === 0) return <p className="text-slate-500">暂无数据</p>;

  return (
    <div className="space-y-4">
      {data.map((item, idx) => {
        const answerKey = `mistake-${idx}`;
        return (
          <div key={idx} className="p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-5 h-5 text-red-500" />
              <span className="text-sm font-medium text-red-700">{item.errorType}</span>
            </div>
            <p className="text-slate-800 mb-2">{item.question}</p>
            
            {item.options && (
              <div className="space-y-1 mb-2">
                {item.options.map((opt: string, optIdx: number) => (
                  <div key={optIdx} className="text-sm text-slate-600 pl-4">{opt}</div>
                ))}
              </div>
            )}

            <button
              onClick={() => toggleAnswer(answerKey)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showAnswer[answerKey] ? '隐藏解析' : '显示解析'}
            </button>

            {showAnswer[answerKey] && (
              <div className="mt-3 space-y-2">
                <div className="p-2 bg-red-100 rounded text-sm">
                  <span className="font-medium text-red-800">常见错误: {item.wrongAnswer}</span>
                </div>
                <div className="p-2 bg-green-100 rounded text-sm">
                  <span className="font-medium text-green-800">正确答案: {item.correctAnswer}</span>
                </div>
                <div className="p-2 bg-slate-100 rounded text-sm">
                  <p className="text-slate-700">{item.errorAnalysis}</p>
                </div>
                <div className="p-2 bg-blue-50 rounded text-sm">
                  <p className="text-blue-800">{item.correctSolution}</p>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Difficulties Section Component
function DifficultiesSection({ data }: { data: any[] }) {
  if (!data || data.length === 0) return <p className="text-slate-500">暂无数据</p>;

  return (
    <div className="space-y-6">
      {data.map((item, idx) => (
        <div key={idx} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-amber-900">{item.title}</h3>
          </div>
          
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-1">难点解析</h4>
              <p className="text-slate-600">{item.breakdown}</p>
            </div>

            {item.tips && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-1">学习技巧</h4>
                <ul className="space-y-1">
                  {item.tips.map((tip: string, tIdx: number) => (
                    <li key={tIdx} className="text-slate-600 text-sm">• {tip}</li>
                  ))}
                </ul>
              </div>
            )}

            {item.ibStrategy && (
              <div className="p-3 bg-blue-50 rounded border border-blue-200">
                <h4 className="text-sm font-medium text-blue-800 mb-1">IB答题策略</h4>
                <p className="text-sm text-blue-700">{item.ibStrategy}</p>
              </div>
            )}

            {item.example && (
              <div className="p-3 bg-white rounded border border-slate-200">
                <h4 className="text-sm font-medium text-slate-700 mb-2">典型例题</h4>
                <p className="text-slate-800 mb-2">{item.example.question}</p>
                <p className="text-sm text-slate-600 mb-2">{item.example.solution}</p>
                {item.example.keyPoints && (
                  <div className="text-sm text-green-700">
                    关键点: {item.example.keyPoints.join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default StudyProgramPage;
